/** The wizard's step indicator — the app had none before this, so it is built here rather than
 *  borrowed. Modelled on the pill-row idiom in `dashboard/coach/TabsRow.js` and styled from
 *  `coachTokens`, which is what the newest client pages use.
 *
 *  Hidden below `md`: a six-row rail with hints does not fit a 375px screen, and the wizard renders
 *  a one-line "שלב N מתוך M" progress bar there instead. */

import { Box, CircularProgress } from "@mui/material";
import {
  Check as CheckIcon,
  PriorityHigh as FailedIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import { t } from "../../dashboard/coach/coachTokens";
import {
  STEP_HINTS,
  STEP_LABELS,
  type StepId,
  type StepStatus,
  type WizardState,
} from "./wizardReducer";

/** The badge that replaces the step number once a step has an outcome. */
const badgeContent = (status: StepStatus, position: number) => {
  if (status === "saving") return <CircularProgress size={13} sx={{ color: t.green }} />;
  if (status === "saved") return <CheckIcon sx={{ fontSize: "16px" }} />;
  if (status === "skipped") return <RemoveIcon sx={{ fontSize: "16px" }} />;
  if (status === "failed") return <FailedIcon sx={{ fontSize: "15px" }} />;
  // `ready` keeps its number: in create mode nothing is written until the end, and a ✓ there
  // would claim a save that has not happened.
  return position;
};

const badgeColors = (status: StepStatus, isCurrent: boolean) => {
  if (isCurrent) return { backgroundColor: t.green, borderColor: t.green, color: t.white };
  if (status === "saved" || status === "ready") {
    return { backgroundColor: t.greenLight, borderColor: "transparent", color: t.greenText };
  }
  if (status === "failed") {
    return { backgroundColor: t.redLight, borderColor: t.red, color: t.redText };
  }
  if (status === "skipped") {
    return { backgroundColor: t.bg, borderColor: t.border, color: t.t3 };
  }
  return { backgroundColor: t.white, borderColor: t.border, color: t.t3 };
};

/** The sub-label under a step name, when its outcome says more than the hint would. */
const statusHint = (status: StepStatus): string | null => {
  if (status === "skipped") return "דולג";
  if (status === "ready") return "מוכן";
  if (status === "failed") return "נכשל";
  return null;
};

interface StepRailProps {
  /** Every step, so the numbering is continuous — including ones that cannot be jumped to. */
  steps: StepId[];
  /** The subset that can be jumped to; identity is drawn but not navigable once the client exists. */
  navigable: StepId[];
  current: StepId;
  stepStates: WizardState["steps"];
  /** False until the trainee row exists — nothing downstream can be opened before then. */
  canNavigate: boolean;
  onSelect: (step: StepId) => void;
}

const StepRail = ({
  steps,
  navigable,
  current,
  stepStates,
  canNavigate,
  onSelect,
}: StepRailProps) => (
  <Box
    component="nav"
    aria-label="שלבי הגדרת הלקוח"
    sx={{
      display: { xs: "none", md: "flex" },
      flexDirection: "column",
      gap: "2px",
      width: "240px",
      flexShrink: 0,
    }}
  >
    {steps.map((step, index) => {
      const status = stepStates[step].status;
      const isCurrent = step === current;
      const clickable = canNavigate && !isCurrent && navigable.includes(step);
      const colors = badgeColors(status, isCurrent);

      return (
        <Box
          key={step}
          component="button"
          type="button"
          disabled={!clickable}
          onClick={() => onSelect(step)}
          aria-current={isCurrent ? "step" : undefined}
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
            width: "100%",
            padding: "10px 12px",
            border: "none",
            borderRadius: t.rs,
            backgroundColor: isCurrent ? t.greenLight : "transparent",
            cursor: clickable ? "pointer" : "default",
            fontFamily: "inherit",
            textAlign: "start",
            transition: "background-color .13s",
            "&:hover": clickable ? { backgroundColor: t.bg } : undefined,
            // The connector between badges. `insetInlineStart` keeps it under the badge in both
            // directions — a hardcoded `left` would sit on the labels once stylis flips the row.
            "&:not(:last-of-type)::after": {
              content: '""',
              position: "absolute",
              insetInlineStart: "25px",
              top: "38px",
              bottom: "-2px",
              width: "2px",
              backgroundColor:
                status === "saved" || status === "ready" ? t.greenLight : t.borderLight,
            },
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              width: "26px",
              height: "26px",
              borderRadius: "50%",
              border: `1px solid ${colors.borderColor}`,
              backgroundColor: colors.backgroundColor,
              color: colors.color,
              fontSize: "12px",
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {badgeContent(status, index + 1)}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                fontSize: "13px",
                fontWeight: isCurrent ? 700 : 600,
                color: isCurrent ? t.greenText : t.text,
              }}
            >
              {STEP_LABELS[step]}
            </Box>
            <Box sx={{ fontSize: "11px", color: t.t3, marginTop: "2px", lineHeight: 1.4 }}>
              {statusHint(status) ?? STEP_HINTS[step]}
            </Box>
          </Box>
        </Box>
      );
    })}
  </Box>
);

export default StepRail;
